import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import _ from "lodash";
import deviceAlertServices from "app/services/automations/device-alert-services";
import AutomationMetaHeader from "../../components/AutomationMetaHeader";
import EditorSaveBar from "../../components/EditorSaveBar";
import NotifyAfterField from "../../components/NotifyAfterField";
import ScheduleEditor from "../../components/ScheduleEditor";
import SectionCard from "../../components/SectionCard";
import { deviceOptionLabel, groupedDeviceOptions } from "../../components/deviceOptions";
import {
    CHANNEL_LABELS,
    COMMAND_TYPE_LABELS,
    CONDITION_ROLE_FILTER,
    CONDITION_ROLE_TRIGGER,
    CONNECTOR_LABELS,
    COOLDOWN_OPTIONS,
    DEFAULT_COOLDOWN_SECONDS,
    DEFAULT_RECOVERY_AFTER_SECONDS,
    DESIRED_STATE_LABELS,
    MIN_COOLDOWN_SECONDS,
    OPERATOR_LABELS,
    RECOVERY_MINUTES_RANGE,
    TRIGGER_CONNECTOR_LABELS,
    VALUE_KIND_LABELS,
    detectSchedulePreset,
    hasScheduleWindow,
    humanDuration,
} from "./alertRuleConstants";

const isWholeMinutesInRange = (value, min, max) => {
    const number = Number(value);
    return value !== "" && Number.isInteger(number) && number >= min && number <= max;
};

const renderSectionCard = ({ children, ...props }) => <SectionCard {...props}>{children}</SectionCard>;

// Editor de una regla («Avisar o actuar cuando pase algo»). Es el formulario de la antigua DeviceAlertRulesIndex, sin
// cambios de comportamiento: ahora entra por ruta (crear, editar, duplicar o un borrador del asistente) y al guardar o
// cancelar vuelve a quien lo abrió.
const AlertRuleEditor = ({ id, duplicateFromId, query = {}, assistantDraft, groupOptions = [], onSaved, onCancel, setAssistantContext }) => {
    const sourceDeviceFilterId = query.device_id || null;

    const [editOptions, setEditOptions] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [formRule, setFormRule] = useState(null);
    const [removedConditionIds, setRemovedConditionIds] = useState([]);
    const [removedRecipientIds, setRemovedRecipientIds] = useState([]);
    const [removedActionIds, setRemovedActionIds] = useState([]);
    // Al guardar se vuelve a la lista: el editor ya no existe cuando termina la petición.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const conditionDevices = useMemo(() => editOptions?.condition_devices || [], [editOptions]);
    const actionTargetDevices = useMemo(() => editOptions?.action_target_devices || [], [editOptions]);
    const users = useMemo(() => editOptions?.users || [], [editOptions]);
    const operators = useMemo(() => editOptions?.operators || [], [editOptions]);
    const channels = useMemo(() => editOptions?.channels || [], [editOptions]);
    const conditionsOperators = useMemo(() => editOptions?.conditions_operators || ["all", "any"], [editOptions]);
    const commandTypes = useMemo(() => editOptions?.action_command_types || ["set_state"], [editOptions]);
    const desiredStates = useMemo(() => editOptions?.action_desired_states || ["ON", "OFF"], [editOptions]);

    const conditionDevicesById = useMemo(() => _.keyBy(conditionDevices, "id"), [conditionDevices]);
    const usersById = useMemo(() => _.keyBy(users, "id"), [users]);

    const selectedFilterDevice = useMemo(() => {
        if (!sourceDeviceFilterId) return null;
        return conditionDevicesById[sourceDeviceFilterId] || _.find(conditionDevices, (device) => `${device.id}` === `${sourceDeviceFilterId}`) || null;
    }, [conditionDevices, conditionDevicesById, sourceDeviceFilterId]);

    const defaultConditionDeviceId = useMemo(() => {
        if (selectedFilterDevice?.id) return selectedFilterDevice.id;
        return conditionDevices[0]?.id || null;
    }, [selectedFilterDevice, conditionDevices]);

    // Las salidas con estado seguro las maneja un control «Mantener en rango»: una acción nueva nunca parte en ellas.
    const firstFreeTargetId = useMemo(() => _.find(actionTargetDevices, (device) => !device.supervised)?.id || null, [actionTargetDevices]);

    const measurementTypesForDevice = useCallback((deviceId) => {
        if (!deviceId) return [];
        const selectedDevice = conditionDevicesById[deviceId] || _.find(conditionDevices, (device) => `${device.id}` === `${deviceId}`);
        return selectedDevice?.measurement_types || [];
    }, [conditionDevices, conditionDevicesById]);

    const conditionRole = useCallback((condition) => condition?.condition_role || CONDITION_ROLE_TRIGGER, []);

    const conditionsByRole = useCallback((conditions, role) => {
        return _.filter(conditions || [], (condition) => conditionRole(condition) === role);
    }, [conditionRole]);

    const isUnavailableRecipientUser = useCallback((recipient) => {
        if (!recipient?.user_id) return false;
        return !usersById[recipient.user_id];
    }, [usersById]);

    const recipientUserLabel = useCallback((recipient) => {
        const activeUser = usersById[recipient?.user_id];
        if (recipient?.user_name) return recipient.user_name;
        if (recipient?.user_email) return recipient.user_email;
        if (activeUser?.full_name) return activeUser.full_name;
        if (activeUser?.email) return activeUser.email;
        return recipient?.user_id ? `Usuario #${recipient.user_id}` : "Usuario no disponible";
    }, [usersById]);

    const buildBlankCondition = useCallback((role = CONDITION_ROLE_TRIGGER) => {
        const conditionDeviceId = defaultConditionDeviceId;
        const measurementTypes = measurementTypesForDevice(conditionDeviceId);
        return {
            id: null,
            condition_role: role,
            device_id: conditionDeviceId,
            measurement_type_id: measurementTypes[0]?.id || null,
            operator: operators[0] || "gt",
            value_kind: "numeric",
            threshold_numeric: "",
            threshold_text: "",
            position: 1,
        };
    }, [defaultConditionDeviceId, measurementTypesForDevice, operators]);

    const buildBlankRecipient = useCallback(() => ({
        id: null,
        user_id: users[0]?.id || null,
        channel: channels[0] || "email",
        destination: "",
        enabled: true,
        notify_after_seconds: 0,
    }), [channels, users]);

    const buildBlankAction = useCallback(() => ({
        id: null,
        target_device_id: firstFreeTargetId,
        command_type: commandTypes[0] || "set_state",
        desired_state: desiredStates[0] || "ON",
        enabled: true,
        position: 1,
    }), [commandTypes, desiredStates, firstFreeTargetId]);

    const buildBlankRule = useCallback(() => ({
        id: null,
        name: "",
        description: "",
        notification_group: "",
        enabled: true,
        conditions_operator: conditionsOperators[0] || "all",
        trigger_conditions_operator: conditionsOperators[0] || "all",
        cooldown_seconds: DEFAULT_COOLDOWN_SECONDS,
        notify_recovery: false,
        recovery_minutes: DEFAULT_RECOVERY_AFTER_SECONDS / 60,
        schedule_start_minute: null,
        schedule_end_minute: null,
        schedule_days: [],
        rule_conditions: [buildBlankCondition(CONDITION_ROLE_TRIGGER)],
        rule_recipients: [],
        rule_device_actions: [],
    }), [buildBlankCondition, conditionsOperators]);

    const normalizeCondition = useCallback((condition, index) => {
        const conditionDeviceId = condition?.device_id || defaultConditionDeviceId;
        const measurementTypes = measurementTypesForDevice(conditionDeviceId);
        return {
            id: condition?.id || null,
            condition_role: condition?.condition_role || CONDITION_ROLE_TRIGGER,
            device_id: conditionDeviceId,
            device_name: condition?.device_name || "",
            measurement_type_id: condition?.measurement_type_id || measurementTypes[0]?.id || null,
            measurement_name: condition?.measurement_name || "",
            operator: condition?.operator || operators[0] || "gt",
            value_kind: condition?.value_kind || "numeric",
            threshold_numeric: _.isNil(condition?.threshold_numeric) ? "" : `${condition.threshold_numeric}`,
            threshold_text: condition?.threshold_text || "",
            position: condition?.position || index + 1,
        };
    }, [defaultConditionDeviceId, measurementTypesForDevice, operators]);

    const normalizeRule = useCallback((rule) => ({
        ...rule,
        id: rule?.id || null,
        name: rule?.name || "",
        description: rule?.description || "",
        notification_group: rule?.notification_group || "",
        enabled: rule?.enabled !== false,
        conditions_operator: rule?.conditions_operator || conditionsOperators[0] || "all",
        trigger_conditions_operator: rule?.trigger_conditions_operator || conditionsOperators[0] || "all",
        cooldown_seconds: Math.max(Number(rule?.cooldown_seconds) || 0, MIN_COOLDOWN_SECONDS),
        notify_recovery: rule?.notify_recovery === true,
        recovery_minutes: Math.round(Number(rule?.recovery_after_seconds || DEFAULT_RECOVERY_AFTER_SECONDS) / 60),
        schedule_start_minute: _.isNil(rule?.schedule_start_minute) ? null : Number(rule.schedule_start_minute),
        schedule_end_minute: _.isNil(rule?.schedule_end_minute) ? null : Number(rule.schedule_end_minute),
        schedule_days: _.isArray(rule?.schedule_days) ? rule.schedule_days : [],
        rule_conditions: _.map(rule?.rule_conditions || [], normalizeCondition),
        rule_recipients: _.map(rule?.rule_recipients || [], (recipient) => ({
            id: recipient?.id || null,
            user_id: recipient?.user_id || users[0]?.id || null,
            channel: recipient?.channel || channels[0] || "email",
            destination: recipient?.destination || "",
            enabled: recipient?.enabled !== false,
            notify_after_seconds: Number(recipient?.notify_after_seconds) || 0,
            user_name: recipient?.user_name,
            user_email: recipient?.user_email,
        })),
        rule_device_actions: _.map(rule?.rule_device_actions || [], (action, index) => ({
            id: action?.id || null,
            target_device_id: action?.target_device_id || firstFreeTargetId,
            command_type: action?.command_type || commandTypes[0] || "set_state",
            desired_state: action?.desired_state || desiredStates[0] || "ON",
            enabled: action?.enabled !== false,
            position: action?.position || index + 1,
            target_device_name: action?.target_device_name,
        })),
    }), [channels, commandTypes, conditionsOperators, desiredStates, firstFreeTargetId, normalizeCondition, users]);

    useEffect(() => {
        setFormRule((prev) => {
            if (!prev) return prev;

            const nextConditions = _.map(prev.rule_conditions || [], (condition) => {
                const conditionDeviceId = condition?.device_id || defaultConditionDeviceId;
                const measurementTypes = measurementTypesForDevice(conditionDeviceId);
                const selectedMeasurementStillValid = _.some(measurementTypes, { id: condition?.measurement_type_id });
                const nextMeasurementTypeId = selectedMeasurementStillValid ? condition?.measurement_type_id : (measurementTypes[0]?.id || null);

                if (conditionDeviceId !== condition?.device_id || nextMeasurementTypeId !== condition?.measurement_type_id) {
                    return {
                        ...condition,
                        device_id: conditionDeviceId,
                        measurement_type_id: nextMeasurementTypeId,
                    };
                }

                return condition;
            });

            if (_.isEqual(nextConditions, prev.rule_conditions || [])) return prev;

            return {
                ...prev,
                rule_conditions: nextConditions,
            };
        });
    }, [defaultConditionDeviceId, measurementTypesForDevice]);

    const normalizeAssistantRuleDraft = useCallback((message) => {
        const payload = message?.draft_payload || {};
        const draftPayload = payload.device_alert_rule || payload.rule || payload;
        const modeFromDraft = payload.mode || "create";
        const isUpdateDraft = modeFromDraft === "update";
        const draftConditions = draftPayload.rule_conditions || draftPayload.rule_conditions_attributes || [];
        const draftRecipients = draftPayload.rule_recipients || draftPayload.rule_recipients_attributes || [];
        const draftActions = draftPayload.rule_device_actions || draftPayload.rule_device_actions_attributes || [];

        const nextRule = normalizeRule({
            ...draftPayload,
            id: isUpdateDraft ? (draftPayload.id || payload.target_id || null) : null,
            enabled: draftPayload.enabled === true,
            rule_conditions: _.filter(draftConditions, (condition) => !condition?._destroy).map((condition, index) => ({
                ...condition,
                id: isUpdateDraft ? condition?.id || null : null,
                condition_role: condition?.condition_role || (index === 0 ? CONDITION_ROLE_TRIGGER : CONDITION_ROLE_FILTER),
            })),
            rule_recipients: _.filter(draftRecipients, (recipient) => !recipient?._destroy).map((recipient) => ({
                ...recipient,
                id: isUpdateDraft ? recipient?.id || null : null,
            })),
            rule_device_actions: _.filter(draftActions, (action) => !action?._destroy).map((action, index) => ({
                ...action,
                id: isUpdateDraft ? action?.id || null : null,
                position: action?.position || index + 1,
            })),
        });

        if (!conditionsByRole(nextRule.rule_conditions, CONDITION_ROLE_TRIGGER).length) {
            nextRule.rule_conditions = [
                buildBlankCondition(CONDITION_ROLE_TRIGGER),
                ...(nextRule.rule_conditions || []),
            ];
        }

        return nextRule;
    }, [buildBlankCondition, conditionsByRole, normalizeRule]);

    useEffect(() => {
        let active = true;
        deviceAlertServices.getRuleEditOptions()
            .then((resp) => { if (active) setEditOptions(resp || {}); })
            .catch(() => { if (active) setLoadError("No se pudieron cargar las opciones del formulario."); });
        return () => { active = false; };
    }, []);

    // Crear, editar, duplicar o un borrador del asistente. Espera las opciones de edición: los valores por defecto
    // (primer usuario, canal, dispositivo) salen de ahí, y con un enlace directo todavía no estarían.
    useEffect(() => {
        if (!editOptions || initialized) return undefined;
        let active = true;
        const finish = (rule) => {
            if (!active) return;
            setFormRule(rule);
            setInitialized(true);
        };

        if (assistantDraft) {
            // Al editar, el borrador marca con _destroy los disparadores y destinatarios que quita: se borran al guardar.
            const payload = assistantDraft?.draft_payload || {};
            const draftRule = payload.device_alert_rule || payload.rule || payload;
            const destroyedIds = (items) => _.map(_.filter(items, (item) => item?._destroy && item?.id), "id");
            if (payload.mode === "update") {
                setRemovedConditionIds(destroyedIds(draftRule.rule_conditions));
                setRemovedRecipientIds(destroyedIds(draftRule.rule_recipients));
                setRemovedActionIds(destroyedIds(draftRule.rule_device_actions));
            }
            finish(normalizeAssistantRuleDraft(assistantDraft));
        } else if (id || duplicateFromId) {
            deviceAlertServices.getRule(id || duplicateFromId)
                .then((resp) => {
                    const normalizedRule = normalizeRule(resp);
                    if (id) return finish(normalizedRule);

                    finish({
                        ...normalizedRule,
                        id: null,
                        name: `Copia de ${normalizedRule.name || `Automatización #${duplicateFromId}`}`,
                        enabled: false,
                        last_triggered_at: null,
                        rule_conditions: _.map(normalizedRule.rule_conditions || [], (condition, index) => ({
                            ...condition,
                            id: null,
                            position: index + 1,
                        })),
                        rule_recipients: _.map(normalizedRule.rule_recipients || [], (recipient) => ({
                            ...recipient,
                            id: null,
                        })),
                        rule_device_actions: _.map(normalizedRule.rule_device_actions || [], (action, index) => ({
                            ...action,
                            id: null,
                            position: index + 1,
                        })),
                    });
                })
                .catch(() => { if (active) setLoadError("No se encontró la automatización."); });
        } else {
            finish(buildBlankRule());
        }

        return () => { active = false; };
    }, [assistantDraft, buildBlankRule, duplicateFromId, editOptions, id, initialized, normalizeAssistantRuleDraft, normalizeRule]);

    useEffect(() => {
        setAssistantContext?.({
            mode: "form",
            device_id: sourceDeviceFilterId,
            current_rule: formRule ? {
                id: formRule.id,
                name: formRule.name,
                enabled: formRule.enabled,
                notification_group: formRule.notification_group,
                conditions_operator: formRule.conditions_operator,
                trigger_conditions_operator: formRule.trigger_conditions_operator,
                cooldown_seconds: formRule.cooldown_seconds,
                notify_recovery: formRule.notify_recovery,
                recovery_after_seconds: Number(formRule.recovery_minutes) * 60,
                rule_recipients: _.map(formRule.rule_recipients, (recipient) => _.pick(recipient, ["user_id", "channel", "notify_after_seconds"])),
            } : null,
        });
    }, [formRule, setAssistantContext, sourceDeviceFilterId]);

    const updateFormRule = (patch) => {
        setFormRule((prev) => ({ ...prev, ...patch }));
    };

    const updateCondition = (index, patch) => {
        setFormRule((prev) => ({
            ...prev,
            rule_conditions: _.map(prev.rule_conditions, (condition, conditionIndex) => {
                if (conditionIndex !== index) return condition;

                const nextCondition = { ...condition, ...patch };
                if (Object.prototype.hasOwnProperty.call(patch, "device_id")) {
                    const measurementTypes = measurementTypesForDevice(patch.device_id);
                    nextCondition.measurement_type_id = measurementTypes[0]?.id || null;
                }

                return nextCondition;
            }),
        }));
    };

    const addCondition = (role = CONDITION_ROLE_TRIGGER) => {
        setFormRule((prev) => ({
            ...prev,
            rule_conditions: [
                ...prev.rule_conditions,
                {
                    ...buildBlankCondition(role),
                    device_id: _.last(conditionsByRole(prev.rule_conditions, role))?.device_id || defaultConditionDeviceId,
                    position: conditionsByRole(prev.rule_conditions, role).length + 1,
                },
            ],
        }));
    };

    const removeCondition = (index) => {
        if (!formRule?.rule_conditions) return;
        const target = formRule.rule_conditions[index];
        if (target?.id) {
            setRemovedConditionIds((prev) => _.uniq([...prev, target.id]));
        }

        const nextConditions = _.filter(formRule.rule_conditions, (_condition, conditionIndex) => conditionIndex !== index);
        const nextConditionsWithRequiredTrigger = conditionsByRole(nextConditions, CONDITION_ROLE_TRIGGER).length
            ? nextConditions
            : [...nextConditions, buildBlankCondition(CONDITION_ROLE_TRIGGER)];

        setFormRule({
            ...formRule,
            rule_conditions: nextConditionsWithRequiredTrigger,
        });
    };

    const updateRecipient = (index, patch) => {
        setFormRule((prev) => ({
            ...prev,
            rule_recipients: _.map(prev.rule_recipients, (recipient, recipientIndex) => {
                if (recipientIndex !== index) return recipient;
                return { ...recipient, ...patch };
            }),
        }));
    };

    const addRecipient = () => {
        setFormRule((prev) => ({
            ...prev,
            rule_recipients: [
                ...prev.rule_recipients,
                buildBlankRecipient(),
            ],
        }));
    };

    const removeRecipient = (index) => {
        if (!formRule?.rule_recipients) return;
        const target = formRule.rule_recipients[index];
        if (target?.id) {
            setRemovedRecipientIds((prev) => _.uniq([...prev, target.id]));
        }

        const nextRecipients = _.filter(formRule.rule_recipients, (_recipient, recipientIndex) => recipientIndex !== index);
        setFormRule({
            ...formRule,
            rule_recipients: nextRecipients,
        });
    };

    const updateAction = (index, patch) => {
        setFormRule((prev) => ({
            ...prev,
            rule_device_actions: _.map(prev.rule_device_actions, (action, actionIndex) => {
                if (actionIndex !== index) return action;
                return { ...action, ...patch };
            }),
        }));
    };

    const addAction = () => {
        setFormRule((prev) => ({
            ...prev,
            rule_device_actions: [
                ...prev.rule_device_actions,
                {
                    ...buildBlankAction(),
                    position: prev.rule_device_actions.length + 1,
                },
            ],
        }));
    };

    const removeAction = (index) => {
        if (!formRule?.rule_device_actions) return;
        const target = formRule.rule_device_actions[index];
        if (target?.id) {
            setRemovedActionIds((prev) => _.uniq([...prev, target.id]));
        }

        const nextActions = _.filter(formRule.rule_device_actions, (_action, actionIndex) => actionIndex !== index);
        setFormRule({
            ...formRule,
            rule_device_actions: nextActions,
        });
    };

    const isConditionComplete = (condition) => {
        if (!condition?.device_id || !condition?.measurement_type_id || !condition?.operator || !condition?.value_kind) return false;
        if (condition.value_kind === "numeric") {
            return condition.threshold_numeric !== "" && !_.isNil(condition.threshold_numeric);
        }
        return !!condition.threshold_text?.toString()?.trim();
    };

    const isRecipientComplete = (recipient) => {
        // NotifyAfterField deja null mientras los minutos escritos no sirven.
        if (!Number.isInteger(recipient?.notify_after_seconds)) return false;
        return !!recipient?.user_id && !!recipient?.channel;
    };

    const isActionComplete = (action) => {
        return !!action?.target_device_id && !!action?.command_type && !!action?.desired_state;
    };

    const canSave = useMemo(() => {
        if (!formRule?.name?.trim()) return false;
        const triggerConditions = conditionsByRole(formRule?.rule_conditions, CONDITION_ROLE_TRIGGER);
        if (!triggerConditions.length) return false;

        if (hasScheduleWindow(formRule) && formRule.schedule_start_minute === formRule.schedule_end_minute) return false;
        if (detectSchedulePreset(formRule) === "custom" && !(formRule.schedule_days || []).length) return false;
        if (formRule.notify_recovery && !isWholeMinutesInRange(formRule.recovery_minutes, RECOVERY_MINUTES_RANGE.min, RECOVERY_MINUTES_RANGE.max)) return false;

        const allConditionsReady = _.every(formRule.rule_conditions || [], isConditionComplete);
        const allRecipientsReady = _.every(formRule.rule_recipients || [], isRecipientComplete);
        const allActionsReady = _.every(formRule.rule_device_actions || [], isActionComplete);
        const hasAtLeastOneActionPath = (formRule.rule_recipients || []).length > 0 || (formRule.rule_device_actions || []).length > 0;

        return allConditionsReady && allRecipientsReady && allActionsReady && hasAtLeastOneActionPath;
    }, [conditionsByRole, formRule]);

    const buildPayload = () => {
        const conditionAttributes = _.flatMap([CONDITION_ROLE_TRIGGER, CONDITION_ROLE_FILTER], (role) => (
            _.map(conditionsByRole(formRule?.rule_conditions || [], role), (condition, index) => ({
                id: condition?.id || undefined,
                condition_role: role,
                device_id: condition?.device_id,
                measurement_type_id: condition?.measurement_type_id,
                operator: condition?.operator,
                value_kind: condition?.value_kind,
                threshold_numeric: condition?.value_kind === "numeric" ? Number(condition?.threshold_numeric) : null,
                threshold_text: condition?.value_kind === "text" ? condition?.threshold_text : null,
                position: index + 1,
            }))
        ));

        const deletedConditionAttributes = _.map(removedConditionIds, (id) => ({ id, _destroy: true }));

        const recipientAttributes = _.map(formRule?.rule_recipients || [], (recipient) => ({
            id: recipient?.id || undefined,
            user_id: recipient?.user_id,
            channel: recipient?.channel,
            destination: recipient?.destination || null,
            enabled: recipient?.enabled !== false,
            notify_after_seconds: recipient?.notify_after_seconds,
        }));

        const deletedRecipientAttributes = _.map(removedRecipientIds, (id) => ({ id, _destroy: true }));

        const actionAttributes = _.map(formRule?.rule_device_actions || [], (action, index) => ({
            id: action?.id || undefined,
            target_device_id: action?.target_device_id,
            command_type: action?.command_type,
            desired_state: action?.desired_state,
            enabled: action?.enabled !== false,
            position: index + 1,
        }));

        const deletedActionAttributes = _.map(removedActionIds, (id) => ({ id, _destroy: true }));

        return {
            device_id: _.find(conditionsByRole(formRule?.rule_conditions || [], CONDITION_ROLE_TRIGGER), (condition) => !!condition?.device_id)?.device_id
                || _.find(formRule?.rule_conditions || [], (condition) => !!condition?.device_id)?.device_id
                || defaultConditionDeviceId,
            name: formRule.name?.trim(),
            description: formRule.description || "",
            notification_group: formRule.notification_group?.trim() || null,
            enabled: formRule.enabled !== false,
            conditions_operator: formRule.conditions_operator || "all",
            trigger_conditions_operator: formRule.trigger_conditions_operator || "all",
            cooldown_seconds: Math.max(Number(formRule.cooldown_seconds) || 0, MIN_COOLDOWN_SECONDS),
            notify_recovery: formRule.notify_recovery === true,
            // Con el aviso apagado igual se guarda: es el margen para que una lectura suelta no reinicie los tiempos.
            recovery_after_seconds: isWholeMinutesInRange(formRule.recovery_minutes, RECOVERY_MINUTES_RANGE.min, RECOVERY_MINUTES_RANGE.max)
                ? Number(formRule.recovery_minutes) * 60
                : DEFAULT_RECOVERY_AFTER_SECONDS,
            schedule_start_minute: hasScheduleWindow(formRule) ? formRule.schedule_start_minute : null,
            schedule_end_minute: hasScheduleWindow(formRule) ? formRule.schedule_end_minute : null,
            schedule_days: formRule.schedule_days || [],
            rule_conditions_attributes: [...conditionAttributes, ...deletedConditionAttributes],
            rule_recipients_attributes: [...recipientAttributes, ...deletedRecipientAttributes],
            rule_device_actions_attributes: [...actionAttributes, ...deletedActionAttributes],
        };
    };

    const saveRule = () => {
        if (!formRule || !canSave) return;

        const payload = { device_alert_rule: buildPayload() };
        const request = formRule?.id
            ? deviceAlertServices.updateRule(formRule.id, "", payload)
            : deviceAlertServices.createRule("", payload);

        setSaving(true);
        setSaveError(null);
        request
            .then((savedRule) => {
                onSaved?.(savedRule);
            })
            .catch((error) => {
                if (!mountedRef.current) return;
                const errors = error?.response?.data?.errors;
                const single = error?.response?.data?.error;
                setSaveError(
                    (Array.isArray(errors) && errors.length && errors.join(". "))
                    || single
                    || "No se pudo guardar la automatización."
                );
            })
            .finally(() => {
                if (mountedRef.current) setSaving(false);
            });
    };

    const renderScheduleRow = () => <ScheduleEditor value={formRule} onChange={updateFormRule} />;

    const renderTextAddButton = ({ children, onClick, startIcon = <AddCircleOutlineOutlinedIcon /> }) => (
        <Button
            size="small"
            variant="text"
            startIcon={startIcon}
            onClick={onClick}
            sx={{ alignSelf: "flex-start", px: 0.5 }}>
            {children}
        </Button>
    );

    const renderFlatConditionRows = (role = CONDITION_ROLE_TRIGGER) => {
        const conditionsWithIndex = _.filter(
            _.map(formRule?.rule_conditions || [], (condition, originalIndex) => ({ condition, originalIndex })),
            ({ condition }) => conditionRole(condition) === role
        );

        return (
            <Stack spacing={1.5}>
                {_.map(conditionsWithIndex, ({ condition, originalIndex }, conditionIndex) => {
                    const measurementTypes = measurementTypesForDevice(condition?.device_id);

                    return (
                        <Box
                            key={`form-condition-${role}-${condition?.id || originalIndex}`}
                            sx={{
                                py: 1.25,
                                borderBottom: conditionIndex < conditionsWithIndex.length - 1 ? "1px solid" : "none",
                                borderColor: "divider",
                            }}>
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Dispositivo"
                                        value={condition?.device_id || ""}
                                        SelectProps={{ renderValue: (value) => deviceOptionLabel(conditionDevicesById[value]) }}
                                        onChange={(e) => updateCondition(originalIndex, { device_id: e.target.value })}>
                                        {groupedDeviceOptions({ devices: conditionDevices, keyPrefix: `condition-device-${role}-${conditionIndex}` })}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Medición"
                                        value={condition?.measurement_type_id || ""}
                                        onChange={(e) => updateCondition(originalIndex, { measurement_type_id: e.target.value })}>
                                        {_.map(measurementTypes, (measurementType) => (
                                            <MenuItem key={`measurement-${role}-${conditionIndex}-${measurementType.id}`} value={measurementType.id}>
                                                {measurementType.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Operador"
                                        value={condition?.operator || operators[0] || "gt"}
                                        onChange={(e) => updateCondition(originalIndex, { operator: e.target.value })}>
                                        {_.map(operators, (operator) => (
                                            <MenuItem key={`operator-${role}-${operator}`} value={operator}>
                                                {OPERATOR_LABELS[operator] || operator}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Tipo valor"
                                        value={condition?.value_kind || "numeric"}
                                        onChange={(e) => updateCondition(originalIndex, { value_kind: e.target.value })}>
                                        {_.map(["numeric", "text"], (valueKind) => (
                                            <MenuItem key={`value-kind-${role}-${valueKind}`} value={valueKind}>
                                                {VALUE_KIND_LABELS[valueKind] || valueKind}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={1}>
                                    {condition?.value_kind === "text" ? (
                                        <TextField
                                            fullWidth
                                            label="Valor"
                                            value={condition?.threshold_text || ""}
                                            onChange={(e) => updateCondition(originalIndex, { threshold_text: e.target.value })}
                                        />
                                    ) : (
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Valor"
                                            value={condition?.threshold_numeric}
                                            onChange={(e) => updateCondition(originalIndex, { threshold_numeric: e.target.value })}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} md={1} textAlign="right">
                                    <Tooltip title={role === CONDITION_ROLE_TRIGGER && conditionsWithIndex.length <= 1 ? "Debe existir al menos un trigger" : "Eliminar"}>
                                        <span>
                                            <IconButton
                                                color="error"
                                                disabled={role === CONDITION_ROLE_TRIGGER && conditionsWithIndex.length <= 1}
                                                onClick={() => removeCondition(originalIndex)}>
                                                <DeleteOutlineOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Box>
                    );
                })}
            </Stack>
        );
    };

    const renderThenRows = () => {
        const recipients = formRule?.rule_recipients || [];
        const actions = formRule?.rule_device_actions || [];
        const totalRows = recipients.length + actions.length;
        const rowSx = (rowIndex) => ({
            py: 1.25,
            borderBottom: rowIndex < totalRows - 1 ? "1px solid" : "none",
            borderColor: "divider",
        });

        return (
            <Stack spacing={0}>
                {_.map(recipients, (recipient, recipientIndex) => (
                    <Box key={`form-recipient-${recipient?.id || recipientIndex}`} sx={rowSx(recipientIndex)}>
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} md={2}>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <NotificationsActiveOutlinedIcon fontSize="small" color="primary" />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Notificación</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {isUnavailableRecipientUser(recipient) ? (
                                    <TextField
                                        fullWidth
                                        label="Usuario"
                                        value={recipientUserLabel(recipient)}
                                        helperText="Usuario desactivado o eliminado."
                                        InputProps={{ readOnly: true }}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        select
                                        label="Usuario"
                                        value={recipient?.user_id || ""}
                                        onChange={(e) => updateRecipient(recipientIndex, { user_id: e.target.value })}>
                                        {_.map(users, (user) => (
                                            <MenuItem key={`recipient-user-${user.id}`} value={user.id}>
                                                {user.full_name || user.email}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Canal"
                                    value={recipient?.channel || channels[0] || "email"}
                                    onChange={(e) => updateRecipient(recipientIndex, { channel: e.target.value })}>
                                    {_.map(channels, (channel) => (
                                        <MenuItem key={`channel-${channel}`} value={channel}>
                                            {CHANNEL_LABELS[channel] || channel}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <NotifyAfterField
                                    value={recipient?.notify_after_seconds}
                                    onChange={(seconds) => updateRecipient(recipientIndex, { notify_after_seconds: seconds })}
                                />
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <FormControlLabel
                                    sx={{ ml: 0 }}
                                    label=""
                                    control={(
                                        <Switch
                                            checked={recipient?.enabled !== false}
                                            onChange={(e) => updateRecipient(recipientIndex, { enabled: e.target.checked })}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={1} textAlign="right">
                                <Tooltip title="Eliminar destinatario">
                                    <IconButton color="error" onClick={() => removeRecipient(recipientIndex)}>
                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                {_.map(actions, (action, actionIndex) => (
                    <Box key={`form-device-action-${action?.id || actionIndex}`} sx={rowSx(recipients.length + actionIndex)}>
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} md={2}>
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <BoltOutlinedIcon fontSize="small" color="warning" />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Acción sobre dispositivo</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Dispositivo objetivo"
                                    value={action?.target_device_id || ""}
                                    SelectProps={{ renderValue: (value) => deviceOptionLabel(_.find(actionTargetDevices, { id: value })) }}
                                    onChange={(e) => updateAction(actionIndex, { target_device_id: e.target.value })}>
                                    {groupedDeviceOptions({
                                        devices: actionTargetDevices,
                                        keyPrefix: `action-target-device-${actionIndex}`,
                                        isDisabled: (device) => !!device.supervised && device.id !== action?.target_device_id,
                                        renderLabel: (device) => (device.supervised ? `${device.name} · con estado seguro: solo para controles «Mantener en rango»` : device.name),
                                    })}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Comando"
                                    value={action?.command_type || commandTypes[0] || "set_state"}
                                    onChange={(e) => updateAction(actionIndex, { command_type: e.target.value })}>
                                    {_.map(commandTypes, (commandType) => (
                                        <MenuItem key={`action-command-type-${commandType}`} value={commandType}>
                                            {COMMAND_TYPE_LABELS[commandType] || commandType}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Estado"
                                    value={action?.desired_state || desiredStates[0] || "ON"}
                                    onChange={(e) => updateAction(actionIndex, { desired_state: e.target.value })}>
                                    {_.map(desiredStates, (desiredState) => (
                                        <MenuItem key={`action-desired-state-${desiredState}`} value={desiredState}>
                                            {DESIRED_STATE_LABELS[desiredState] || desiredState}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <FormControlLabel
                                    sx={{ ml: 0 }}
                                    label=""
                                    control={(
                                        <Switch
                                            checked={action?.enabled !== false}
                                            onChange={(e) => updateAction(actionIndex, { enabled: e.target.checked })}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={1} textAlign="right">
                                <Tooltip title="Eliminar acción">
                                    <IconButton color="error" onClick={() => removeAction(actionIndex)}>
                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Stack>
        );
    };

    const renderRecoveryRow = () => {
        const validMinutes = isWholeMinutesInRange(formRule?.recovery_minutes, RECOVERY_MINUTES_RANGE.min, RECOVERY_MINUTES_RANGE.max);
        return (
            <Stack spacing={0.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                    <FormControlLabel
                        sx={{ ml: 0 }}
                        control={(
                            <Switch
                                checked={formRule?.notify_recovery === true}
                                onChange={(e) => updateFormRule({ notify_recovery: e.target.checked })}
                            />
                        )}
                        label="Avisar cuando vuelva a la normalidad"
                    />
                    {formRule?.notify_recovery && (
                        <TextField
                            size="small"
                            type="number"
                            label="Después de"
                            value={formRule?.recovery_minutes ?? ""}
                            onChange={(e) => updateFormRule({ recovery_minutes: e.target.value })}
                            error={!validMinutes}
                            helperText={validMinutes ? undefined : `Entre ${RECOVERY_MINUTES_RANGE.min} y ${RECOVERY_MINUTES_RANGE.max} minutos`}
                            InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>min en orden</Typography> }}
                            inputProps={{ min: RECOVERY_MINUTES_RANGE.min, max: RECOVERY_MINUTES_RANGE.max, step: 1 }}
                            sx={{ width: 190 }}
                        />
                    )}
                </Stack>
                {formRule?.notify_recovery && (
                    <Typography variant="caption" color="text.secondary">
                        Le llega solo a quienes recibieron algún aviso de esa alerta.
                    </Typography>
                )}
            </Stack>
        );
    };

    const renderRuleForm = () => {
        if (!formRule) return null;

        const currentCooldownSeconds = Math.max(Number(formRule?.cooldown_seconds) || 0, MIN_COOLDOWN_SECONDS);
        const cooldownOptions = _.some(COOLDOWN_OPTIONS, { value: currentCooldownSeconds })
            ? COOLDOWN_OPTIONS
            : _.sortBy([
                ...COOLDOWN_OPTIONS,
                { value: currentCooldownSeconds, label: humanDuration(currentCooldownSeconds) },
            ], "value");

        return (
            <Stack spacing={2.25}>
                <AutomationMetaHeader
                    automation={formRule}
                    onChange={updateFormRule}
                    onBack={onCancel}
                    groupOptions={groupOptions}
                    controls={(
                        <Tooltip title="Mientras siga la alerta, cada cuánto se le repite el aviso a cada destinatario">
                            <TextField
                                select
                                size="small"
                                label="Repetir el aviso cada"
                                value={`${currentCooldownSeconds}`}
                                onChange={(e) => updateFormRule({ cooldown_seconds: Number(e.target.value) })}
                                sx={{ minWidth: 172 }}>
                                {_.map(cooldownOptions, (option) => (
                                    <MenuItem key={`cooldown-option-${option.value}`} value={`${option.value}`}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Tooltip>
                    )}
                />

                {renderSectionCard({
                    title: "Cuando",
                    subtitle: (formRule?.trigger_conditions_operator || "all") === "any"
                        ? "Basta con que se cumpla uno de los disparadores para que tu automatización se gatille."
                        : "Se deben cumplir todas las condiciones de todos los disparadores para que tu automatización se gatille.",
                    icon: <BoltOutlinedIcon />,
                    accent: "warning.main",
                    headerActions: (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={formRule?.trigger_conditions_operator || conditionsOperators[0] || "all"}
                            onChange={(_event, nextOperator) => {
                                if (nextOperator) updateFormRule({ trigger_conditions_operator: nextOperator });
                            }}
                            sx={{
                                "& .MuiToggleButton-root": {
                                    px: 1.5,
                                    py: 0.7,
                                    textTransform: "none",
                                },
                            }}>
                            {_.map(conditionsOperators, (conditionOperator) => (
                                <ToggleButton key={`trigger-operator-${conditionOperator}`} value={conditionOperator}>
                                    {TRIGGER_CONNECTOR_LABELS[conditionOperator] || conditionOperator}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    ),
                    children: (
                        <Stack spacing={1.5}>
                            {renderFlatConditionRows(CONDITION_ROLE_TRIGGER)}
                            {renderTextAddButton({
                                onClick: () => addCondition(CONDITION_ROLE_TRIGGER),
                                children: "Agregar disparador",
                            })}
                        </Stack>
                    ),
                })}

                {renderSectionCard({
                    title: "Y si (opcional)",
                    subtitle: "Filtros opcionales que deben cumplirse antes de ejecutar el resultado.",
                    icon: <FilterAltOutlinedIcon />,
                    accent: "primary.main",
                    headerActions: (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={formRule?.conditions_operator || conditionsOperators[0] || "all"}
                            onChange={(_event, nextOperator) => {
                                if (nextOperator) updateFormRule({ conditions_operator: nextOperator });
                            }}
                            sx={{
                                "& .MuiToggleButton-root": {
                                    px: 1.5,
                                    py: 0.7,
                                    textTransform: "none",
                                },
                            }}>
                            {_.map(conditionsOperators, (conditionOperator) => (
                                <ToggleButton key={`conditions-operator-${conditionOperator}`} value={conditionOperator}>
                                    {CONNECTOR_LABELS[conditionOperator] || conditionOperator}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    ),
                    children: (
                        <Stack spacing={1.5}>
                            {renderScheduleRow()}
                            {renderFlatConditionRows(CONDITION_ROLE_FILTER)}
                            {renderTextAddButton({
                                onClick: () => addCondition(CONDITION_ROLE_FILTER),
                                children: "Agregar filtro",
                            })}
                        </Stack>
                    ),
                })}

                {renderSectionCard({
                    title: "Entonces",
                    subtitle: "Qué debe ocurrir cuando la automatización se active.",
                    icon: <PlayCircleOutlineOutlinedIcon />,
                    accent: "success.main",
                    children: (
                        <Stack spacing={1.5}>
                            {renderThenRows()}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                                {renderTextAddButton({
                                    onClick: addRecipient,
                                    children: "Agregar destinatario",
                                })}
                                {renderTextAddButton({
                                    onClick: addAction,
                                    children: "Agregar acción",
                                })}
                            </Stack>
                            {(formRule.rule_recipients || []).length > 0 && renderRecoveryRow()}
                        </Stack>
                    ),
                })}

                {!!saveError && (
                    <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>
                )}

                {!canSave && (
                    <Alert severity="info">
                        Completa nombre, condiciones y al menos un destinatario o acción para guardar la automatización.
                    </Alert>
                )}
            </Stack>
        );
    };

    if (loadError) return <Alert severity="error">{loadError}</Alert>;
    if (!initialized || !formRule) {
        return (
            <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Stack spacing={2}>
                {renderRuleForm()}
            </Stack>
            <EditorSaveBar saving={saving} disabled={!canSave} onSave={saveRule} />
        </>
    );
};

export default AlertRuleEditor;
